import { apiClient } from './api';
import type {
  PlanTemplate,
  CreateWorkoutPlanInput,
  WorkoutPlanDetail,
  PendingPlanStatus,
  SessionSuccessRate,
  BatchSuccessRates,
  FindPlanForDayResponse,
  AdaptationReviewResponse,
  AdaptationRecommendationsResponse,
  AdaptationRecommendationsHistoryResponse,
  AdaptationRecommendation,
  CalendarPlanSourcesResponse,
  PlanFromSessionTemplate,
} from './types';

export class WorkoutPlanService {
  static async getTemplate(
    programId: number,
    dayNumber: number,
    overrideRotationIndex?: number
  ): Promise<PlanTemplate> {
    const qs = overrideRotationIndex !== undefined
      ? `?overrideRotationIndex=${overrideRotationIndex}`
      : '';
    const response = await apiClient.get<PlanTemplate>(
      `/api/workout-plan/template/${programId}/${dayNumber}${qs}`
    );
    return response.data;
  }

  static async create(input: CreateWorkoutPlanInput): Promise<{ id: number; message: string }> {
    const response = await apiClient.post<{ id: number; message: string }>(
      '/api/workout-plan/create',
      input
    );
    return response.data;
  }

  static async getById(sessionId: number): Promise<WorkoutPlanDetail> {
    const response = await apiClient.get<WorkoutPlanDetail>(
      `/api/workout-plan/${sessionId}`
    );
    return response.data;
  }

  static async update(sessionId: number, input: CreateWorkoutPlanInput): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>(
      `/api/workout-plan/${sessionId}`,
      input
    );
    return response.data;
  }

  static async delete(sessionId: number): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `/api/workout-plan/${sessionId}`
    );
    return response.data;
  }

  static async getPendingStatus(): Promise<PendingPlanStatus> {
    const response = await apiClient.get<PendingPlanStatus>(
      '/api/workout-plan/pending'
    );
    return response.data;
  }

  static async getCalendarSources(
    programId?: number,
    dayNumber?: number,
    limit: number = 20
  ): Promise<CalendarPlanSourcesResponse> {
    const params = new URLSearchParams();
    if (programId !== undefined) params.append('programId', String(programId));
    if (dayNumber !== undefined) params.append('dayNumber', String(dayNumber));
    params.append('limit', String(limit));
    const response = await apiClient.get<CalendarPlanSourcesResponse>(
      `/api/workout-plan/calendar-sources?${params.toString()}`
    );
    return response.data;
  }

  static async getFromSession(
    sourceSessionId: number,
    programId?: number,
    dayNumber?: number
  ): Promise<PlanFromSessionTemplate> {
    const params = new URLSearchParams();
    if (programId !== undefined) params.append('programId', String(programId));
    if (dayNumber !== undefined) params.append('dayNumber', String(dayNumber));
    const qs = params.toString();
    const response = await apiClient.get<PlanFromSessionTemplate>(
      `/api/workout-plan/from-session/${sourceSessionId}${qs ? '?' + qs : ''}`
    );
    return response.data;
  }

  static async getSuccessRate(sessionId: number): Promise<SessionSuccessRate> {
    const response = await apiClient.get<SessionSuccessRate>(
      `/api/workout-plan/success-rate/${sessionId}`
    );
    return response.data;
  }

  static async getBatchSuccessRates(
    dateFrom?: string,
    dateTo?: string,
    day?: number,
    programId?: number
  ): Promise<BatchSuccessRates> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (day !== undefined) params.append('day', day.toString());
    if (programId !== undefined) params.append('programId', programId.toString());
    const qs = params.toString();
    const response = await apiClient.get<BatchSuccessRates>(
      `/api/workout-plan/success-rates${qs ? '?' + qs : ''}`
    );
    return response.data;
  }

  static async findPlanForDay(programId: number, dayNumber: number): Promise<FindPlanForDayResponse> {
    const response = await apiClient.get<FindPlanForDayResponse>(
      `/api/workout-plan/find-for-day/${programId}/${dayNumber}`
    );
    return response.data;
  }

  static async reviewAdaptations(programId: number, dayNumber: number): Promise<AdaptationReviewResponse> {
    const response = await apiClient.post<AdaptationReviewResponse>(
      `/api/workout-plan/${programId}/recommendations/review`,
      { dayNumber }
    );
    return response.data;
  }

  static async getActiveRecommendations(
    programId: number,
    dayNumber?: number
  ): Promise<AdaptationRecommendationsResponse> {
    const params = new URLSearchParams();
    if (dayNumber !== undefined) params.append('dayNumber', String(dayNumber));
    const qs = params.toString();
    const response = await apiClient.get<AdaptationRecommendationsResponse>(
      `/api/workout-plan/${programId}/recommendations${qs ? '?' + qs : ''}`
    );
    return response.data;
  }

  static async getRecommendationsHistory(
    programId: number,
    dayNumber?: number
  ): Promise<AdaptationRecommendationsHistoryResponse> {
    const params = new URLSearchParams();
    if (dayNumber !== undefined) params.append('dayNumber', String(dayNumber));
    const qs = params.toString();
    const response = await apiClient.get<AdaptationRecommendationsHistoryResponse>(
      `/api/workout-plan/${programId}/recommendations/history${qs ? '?' + qs : ''}`
    );
    return response.data;
  }

  static async updateRecommendationStatus(
    recommendationId: number,
    status: 'accepted' | 'rejected',
    rejectionReason?: string
  ): Promise<{ recommendation: AdaptationRecommendation }> {
    const response = await apiClient.post<{ recommendation: AdaptationRecommendation }>(
      `/api/workout-plan/recommendations/${recommendationId}/status`,
      { status, rejectionReason }
    );
    return response.data;
  }
}

export default WorkoutPlanService;
